/**
 * Bảng route của SPA — thay hệ thống route theo thư mục của App Router.
 *
 * Route được SINH TỰ ĐỘNG từ cây `src/app` qua `import.meta.glob`, không gõ
 * tay. Lý do và quy tắc chuyển đổi: xem `routes/build-routes.ts`.
 *
 * Giữ nguyên toàn bộ URL của bản Next — route group `(auth)` không xuất hiện
 * trong URL nên `/login` vẫn là `/login`.
 */

import { lazy, Suspense, type ComponentType } from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  ScrollRestoration,
  type RouteObject,
} from 'react-router';

import { AuthProvider } from '@/lib/auth-context';
import { RequireAuth } from '@/components/auth/require-auth';
import { RouteError } from '@/components/auth/route-error';
import { buildRouteTree, type RouteNode } from '@/routes/build-routes';
import { useSessionCleanup } from '@/hooks/use-session-cleanup';

// Ghi chú: `/login` nằm ở `app/(auth)/login/page.tsx`. Route group `(auth)`
// không vào URL nên bộ sinh cho ra đúng `/login` như bản Next.

/**
 * `eager: false` → mỗi route thành một chunk riêng, tải khi điều hướng tới.
 * Đây là thứ thay `next/dynamic` tự động của App Router; thiếu nó thì toàn bộ
 * hơn 100 trang nằm chung một bundle.
 *
 * Mẫu glob phải là hằng chuỗi — Vite phân tích lúc build, không nhận biến.
 */
const pageModules = import.meta.glob<{ default: ComponentType }>(
  './app/**/page.tsx',
);
const layoutModules = import.meta.glob<{ default: ComponentType }>(
  './app/**/layout.tsx',
);

/** Bỏ tiền tố `./app/` để khoá khớp với quy ước của `build-routes`. */
function stripPrefix(
  modules: Record<string, () => Promise<{ default: ComponentType }>>,
) {
  const out: Record<string, () => Promise<{ default: ComponentType }>> = {};
  for (const [key, loader] of Object.entries(modules)) {
    out[key.replace(/^\.\/app\//, '')] = loader;
  }
  return out;
}

const tree = buildRouteTree(
  stripPrefix(pageModules),
  stripPrefix(layoutModules),
);

/** Chờ chunk của route lazy. Thay `loading.tsx` của App Router. */
function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"
        role="status"
        aria-label="Đang tải"
      />
    </div>
  );
}

/** Đổi một nhánh của cây thành `RouteObject` của React Router. */
function toRouteObjects(node: RouteNode): RouteObject[] {
  const children: RouteObject[] = [];

  if (node.page) {
    const Page = lazy(node.page);
    children.push({ index: true, element: <Page /> });
  }

  for (const child of node.children.values()) {
    const sub = toRouteObjects(child);
    children.push(...sub);
  }

  if (node.layout) {
    const Layout = lazy(node.layout);
    return [
      {
        path: node.segment,
        element: <Layout />,
        children,
      },
    ];
  }

  // Không có layout riêng: gắn thẳng các route con vào cha, giữ nguyên đường
  // dẫn bằng cách thêm tiền tố đoạn của chính nó.
  if (!node.segment) return children;

  return children.map((c) => {
    if (c.index) {
      // Route index của nhánh này trở thành route thường mang tên đoạn.
      const { index: _index, ...rest } = c;
      return { ...rest, path: node.segment } as RouteObject;
    }
    return { ...c, path: `${node.segment}/${c.path ?? ''}`.replace(/\/$/, '') };
  });
}

/**
 * Dọn cache/hàng đợi khi đăng xuất hoặc đổi người dùng (FE-06/FE-21) — phải
 * nằm TRONG `AuthProvider` vì cần `useAuth()`. Tách riêng khỏi `RootLayout`
 * chỉ để đúng quy tắc hook (không gọi `useAuth` ở component chứa Provider
 * của chính nó).
 */
function SessionCleanupEffect() {
  useSessionCleanup();
  return null;
}

/**
 * Layout gốc — thay phần `<Providers>` của `app/layout.tsx`.
 *
 * `AuthProvider` nằm ở đây, KHÔNG ở `main.tsx`: nó gọi `useNavigate()` nên bắt
 * buộc phải ở trong router.
 *
 * `ScrollRestoration` bù lại hành vi Next tự có. Thiếu nó, mở một đơn hàng từ
 * giữa danh sách rồi quay lại sẽ giữ nguyên vị trí cuộn cũ.
 */
function RootLayout() {
  return (
    <AuthProvider>
      <SessionCleanupEffect />
      <ScrollRestoration />
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </AuthProvider>
  );
}

/**
 * Ba nhánh cần đăng nhập, khớp đúng `matcher` của middleware cũ:
 * `/dashboard/*`, `/m/*`, `/kt/*`.
 */
const PROTECTED = new Set(['dashboard', 'm', 'kt']);

const generated = toRouteObjects(tree).map((route) =>
  route.path && PROTECTED.has(route.path)
    ? {
        ...route,
        element: <RequireAuth>{route.element}</RequireAuth>,
      }
    : route,
);

export const router = createBrowserRouter([
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      // `/` → `/dashboard`. Bản Next làm việc này ở middleware (khi có session)
      // và `app/page.tsx`. `RequireAuth` dưới `/dashboard` sẽ đá người chưa
      // đăng nhập về `/login`, nên ở đây chỉ cần một lần chuyển hướng.
      { index: true, element: <Navigate to="/dashboard" replace /> },

      ...generated,

      // 404. App Router không có `not-found.tsx` nào trong repo nên trước đây
      // rơi vào trang mặc định của Next; SPA phải tự lo.
      {
        path: '*',
        element: (
          <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background">
            <p className="text-2xl font-semibold text-foreground">404</p>
            <p className="text-muted-foreground">Không tìm thấy trang này.</p>
            <a href="/dashboard" className="text-primary underline">
              Về trang chủ
            </a>
          </div>
        ),
      },
    ],
  },
]);
